import { format, subYears } from 'date-fns';
import * as React from 'react';
import DatePicker from 'react-mobile-datepicker';
import * as vars from '../../../consts/variables';
import { scrollElementIntoView } from '../../../helpers/scroll';
import { TestSelector } from '../../../helpers/TestSelector';
import { __ } from '../../../helpers/TranslationService';
import { colors } from '../../../theme/colors';
import { CalendarIcon } from '../../../components/Icons/newIcons/CalendarIcon';

interface IState {
    isOpen: boolean;
}

interface IProps {
    wrapperStyle?: any;
    selectWrapperStyle?: React.CSSProperties;
    title?: string;
    value?: Date;
    isMin?: boolean;
    isMax?: boolean;
    placeholder?: string;
    isError?: boolean;
    errorMessage?: string;
    isFirstInvalidFormField?: boolean;
    testSelectorName?: string;
    dateOfDeparture?: number;
    onClick?(event: any): void;
    onCancel?(event: any): void;
    onChange(date: any): void;
}

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        margin: '0 15px 20px',
        position: 'relative',
    },
    title: {
        fontSize: 15,
        color: vars.darkGray,
        position: 'relative',
        background: vars.white,
        display: 'inline-block',
        marginLeft: 10,
        zIndex: 1,
        padding: '0 5px',
    },
    selectWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        marginTop: -8,
        border: `1px solid ${vars.gray}`,
    },
    select: {
        fontSize: 16,
        width: '100%',
        padding: '10px 15px 10px 10px',
        opacity: 1,
        border: 'none',
        color: vars.black,
        background: vars.transparent,
    },
    arrowDown: {
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: `8px solid ${vars.darkGray}`,
        position: 'absolute',
        right: 8,
        top: '40%',
        pointerEvents: 'none',
    },
    icon: {
        float: 'right',
        marginRight: 5,
        fill: colors.gunmetal[50],
        flex: '0 0 auto',
        position: 'relative',
        left: 8,
    },
    placeholder: {
        fontSize: 16,
        color: vars.mediumBlack,
        opacity: 1,
        display: 'inline-block',
    },
    placeholderDateFormat: {
        display: 'inline-block',
    },
};

export class DatePickerSelect extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
        };
    }

    datePickerRef = null;
    getDatePickerRef = (ref) => (this.datePickerRef = ref);

    onClick = () => {
        this.setState({ isOpen: true });
    };

    onCancel = (date: string) => {
        if (date !== undefined) {
            this.props.onChange(null);
        }
        this.setState({ isOpen: false });
    };

    onChange = (date: any) => {
        this.setState({ isOpen: false });
        this.props.onChange(new Date(date));
    };

    componentDidUpdate() {
        if (this.props.isFirstInvalidFormField && this.props.isError) {
            scrollElementIntoView(this.datePickerRef);
        }
    }

    datePickerConfig() {
        const dateFormat: string = __('DD.MM.YYYY', 'dreamlines');
        const regex = /[.\/\-]/;
        const dateSplit = dateFormat.split(regex);
        let dateConfig = {};
        const dayConfig = {
            date: {
                format: 'DD',
                caption: __('Day', 'dreamlines'),
                step: 1,
            },
        };
        const monthConfig = {
            month: {
                format: 'MM',
                caption: __('Month', 'dreamlines'),
                step: 1,
            },
        };
        const yearConfig = {
            year: {
                format: 'YYYY',
                caption: __('Year', 'dreamlines'),
                step: 1,
            },
        };
        for (let pos in dateSplit) {
            if (dateSplit.hasOwnProperty(pos)) {
                switch (dateSplit[pos]) {
                    case 'DD':
                        dateConfig = { ...dateConfig, ...dayConfig };
                        break;
                    case 'MM':
                        dateConfig = { ...dateConfig, ...monthConfig };
                        break;
                    case 'YYYY':
                        dateConfig = { ...dateConfig, ...yearConfig };
                        break;
                }
            }
        }

        return dateConfig;
    }

    getValue(): Date {
        const dateFormat: string = __('DD.MM.YYYY', 'dreamlines');
        if (this.props.value) {
            return this.props.value;
        } else {
            return subYears(new Date(), 1);
        }
    }

    render() {
        const wrapperMargin: any = {
            marginBottom: 20,
        };

        const selectWrapperStyle: any = {
            marginTop: this.props.title ? -8 : 0,
            borderColor: this.props.isError || this.props.errorMessage ? vars.pink : vars.gray,
        };

        const errorMessageStyle: any = {
            color: 'rgb(224, 26, 89)',
            fontSize: '14px',
            marginTop: '4px',
        };

        const datepickerProps = {
            value: this.getValue(),
            isOpen: this.state.isOpen,
            onSelect: this.onChange,
            onCancel: this.onCancel,
            confirmText: __('OK', 'dreamlines'),
            cancelText: __('Reset', 'dreamlines'),
            dateConfig: this.datePickerConfig(),
            min: this.props.isMin
                ? subYears(new Date(this.props.dateOfDeparture * 1000), 17)
                : subYears(new Date(), 120),
            max: this.props.isMax ? new Date() : undefined,
            showHeader: true,
            headerFormat: __('DD.MM.YYYY', 'dreamlines'),
            showCaption: true,
        };

        const styleTitle =
            this.props.title && this.props.title.length
                ? styles.title
                : { ...styles.title, display: 'none' };

        return (
            <div
                ref={this.getDatePickerRef}
                style={{ ...styles.wrapper, ...wrapperMargin, ...this.props.wrapperStyle }}
                {...TestSelector.getSelector(this.props.testSelectorName)}
            >
                <div style={styleTitle}>{this.props.title}</div>
                <div
                    style={{
                        ...styles.selectWrapper,
                        ...selectWrapperStyle,
                        ...this.props.selectWrapperStyle,
                    }}
                >
                    <a className="select-btn" style={styles.select} onClick={this.onClick}>
                        {this.props.value ? (
                            <div style={styles.placeholderDateFormat}>
                                {format(
                                    this.props.value,
                                    __('DD.MM.YYYY', 'dreamlines')
                                        .toLowerCase()
                                        .replace('mm', 'MM'),
                                )}
                            </div>
                        ) : (
                            <div style={styles.placeholder}>{this.props.placeholder}</div>
                        )}

                        <CalendarIcon width={18} height={18} style={styles.icon} />
                    </a>
                    {<DatePicker {...datepickerProps} />}
                </div>
                {this.props.errorMessage ? (
                    <div style={errorMessageStyle}>{this.props.errorMessage}</div>
                ) : null}
            </div>
        );
    }
}
